import { AddLocationAlt } from "@mui/icons-material";
import { Box, IconButton, Tooltip } from "@mui/material";
import { FC, SyntheticEvent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { Address } from "@ryde/controls";
import { useGoogleServices } from "@ryde/redux";
import {
  appLanguageSelector,
  castErrorResult,
  createErrorResult,
  createSuccessResult,
  errors,
  ICoordinate,
  ILocationInfo,
  IPlacePrediction,
  IValidationsYupRoot,
  SimpleResult,
  warnAndToastSimpleResult,
} from "@ryde/shared";
import { SxNavigationMenuTooltip } from "@ryde/theme";

import { AddressBlockMap } from "./address-block-map";
import { AddressBlockRight } from "./address-block-right";

interface IAddressBlockProps {
  value: string | ILocationInfo;
  label?: string | null;
  onChange?: (value: Partial<ILocationInfo>) => void;
  error?: boolean;
  formValidation?: IValidationsYupRoot | null;
  disabled?: boolean;
}

const tooltipStyles = { ...SxNavigationMenuTooltip };

export const AddressBlock: FC<IAddressBlockProps> = ({
  value,
  label,
  onChange,
  error,
  formValidation,
  disabled,
}) => {
  const { getCoordinateByPlaceId, searchPlacesByInput, searchAddressByGeo } =
    useGoogleServices();
  const [currentValue, setCurrentValue] = useState<IPlacePrediction | null>(
    null,
  );
  const [currentOptions, setCurrentOptions] = useState<IPlacePrediction[]>([]);
  const [isMapOpen, setIsMapOpen] = useState<boolean>(false);
  const [currentCoordinate, setCurrentCoordinate] = useState<
    ICoordinate | undefined
  >(undefined);
  const { t } = useTranslation();
  const language = useRecoilValue(appLanguageSelector);

  const fetchDefaultData = useEvent(
    async (
      defaultValue: string,
      defaultCoordinate: ICoordinate | undefined,
    ): Promise<
      SimpleResult<{
        nextCurrentPlace: IPlacePrediction;
        nextCurrentCoordinate: ICoordinate | undefined;
      }>
    > => {
      let placesResults = null;

      try {
        if (defaultCoordinate) {
          const searchByGeoResponse = await searchAddressByGeo({
            geoParams: {
              location: defaultCoordinate,
              language,
            },
          });

          placesResults = {
            data: searchByGeoResponse?.data ?? null,
            success: !!searchByGeoResponse?.success,
            ...(searchByGeoResponse?.error
              ? { error: searchByGeoResponse.error }
              : {}),
          };
        } else {
          const searchByInputResponse = await searchPlacesByInput({
            inputParams: {
              input: defaultValue,
              language,
            },
          });

          placesResults = {
            data: searchByInputResponse?.data?.[0] ?? null,
            success: !!searchByInputResponse?.success,
            ...(searchByInputResponse?.error
              ? { error: searchByInputResponse.error }
              : {}),
          };
        }

        if (!placesResults.success) {
          return castErrorResult(placesResults);
        }

        if (!placesResults.data) {
          return createErrorResult(
            errors.map.searchPlacesByInput,
            "No results",
          );
        }

        const nextCurrentPlace = placesResults.data;
        let nextCurrentCoordinate: ICoordinate | undefined = defaultCoordinate;

        if (!defaultCoordinate) {
          const coordinateResult = await getCoordinateByPlaceId(
            placesResults.data?.placeId,
          );

          if (!coordinateResult.success) {
            return castErrorResult(coordinateResult);
          }

          nextCurrentCoordinate = coordinateResult.data;
        }

        return createSuccessResult({
          nextCurrentPlace,
          nextCurrentCoordinate,
        });
      } catch (_err) {
        return createErrorResult(
          errors.common.technicalProblemOccurred,
          "Error while fetching location",
        );
      }
    },
  );

  const syncAddressFromFormValue = useEvent(
    async ({
      defaultValue,
      defaultCoordinate,
      isCancelled,
    }: {
      defaultValue: string;
      defaultCoordinate: ICoordinate | undefined;
      isCancelled: () => boolean;
    }): Promise<void> => {
      if (isCancelled()) {
        return;
      }

      if (defaultCoordinate) {
        setCurrentValue({ text: { text: defaultValue } } as IPlacePrediction);
        setCurrentCoordinate(defaultCoordinate);
        return;
      }

      if (!defaultValue) {
        setCurrentValue(null);
        setCurrentCoordinate(undefined);
        return;
      }

      setCurrentValue({ text: { text: defaultValue } } as IPlacePrediction);

      const dataResult = await fetchDefaultData(defaultValue, undefined);

      if (isCancelled()) {
        return;
      }

      if (!dataResult.success || !dataResult.data) {
        warnAndToastSimpleResult(dataResult, t);
        return;
      }

      setCurrentValue(dataResult.data.nextCurrentPlace);
      setCurrentCoordinate(dataResult.data.nextCurrentCoordinate);
    },
  );

  const valueFullAddress =
    typeof value === "string" ? value : (value?.fullAddress ?? "");
  const valueLatitude = typeof value === "string" ? undefined : value?.latitude;
  const valueLongitude =
    typeof value === "string" ? undefined : value?.longitude;

  useEffect(() => {
    let isCancelled = false;

    const defaultCoordinate =
      valueLatitude && valueLongitude
        ? { lat: valueLatitude, lng: valueLongitude }
        : undefined;

    void syncAddressFromFormValue({
      defaultValue: valueFullAddress,
      defaultCoordinate,
      isCancelled: () => isCancelled,
    });

    return () => {
      isCancelled = true;
    };
  }, [
    syncAddressFromFormValue,
    valueFullAddress,
    valueLatitude,
    valueLongitude,
  ]);

  const onMapOpen = (e: SyntheticEvent): void => {
    e.preventDefault();
    setIsMapOpen(true);
  };

  const handleCurrentValueChange = async (
    newValue: IPlacePrediction | null,
  ): Promise<void> => {
    setCurrentValue(newValue);

    if (newValue) {
      const locationResult = await getCoordinateByPlaceId(newValue?.placeId);

      if (!locationResult.success) {
        return warnAndToastSimpleResult(locationResult, t);
      }

      setCurrentCoordinate(locationResult.data);
      handleParentUpdate(
        newValue?.text?.text,
        locationResult.data?.lat,
        locationResult.data?.lng,
      );
      return;
    }

    setCurrentCoordinate(undefined);
    handleParentUpdate(undefined, undefined, undefined);
  };

  const handleOptionsChange = useCallback((options: IPlacePrediction[]) => {
    setCurrentOptions(options);
  }, []);

  const handleClose = (): void => {
    setIsMapOpen(false);
  };

  const handleSave = (
    nextValue: IPlacePrediction,
    nextCoordinate: ICoordinate,
  ): void => {
    setIsMapOpen(false);
    setCurrentValue(nextValue);
    setCurrentCoordinate(nextCoordinate);
    handleParentUpdate(
      nextValue.text?.text,
      nextCoordinate.lat,
      nextCoordinate.lng,
    );
  };

  const handleParentUpdate = (
    fullAddress: string | undefined,
    latitude: number | undefined,
    longitude: number | undefined,
  ): void => {
    onChange?.({
      fullAddress,
      latitude,
      longitude,
    });
  };

  return (
    <Box>
      <Box sx={{ display: "flex", width: "calc(100% + 48px)" }}>
        <Box sx={{ width: "100%" }}>
          <Address
            isLatLngMissing={!currentCoordinate}
            value={currentValue}
            defaultOptions={currentOptions}
            onChange={handleCurrentValueChange}
            onOptionsChange={handleOptionsChange}
            label={label}
            sx={{ marginBottom: "5px", width: "100%" }}
            error={error}
            formValidation={formValidation}
            disabled={disabled}
          />
        </Box>
        <Box sx={{ paddingInlineStart: "8px", paddingTop: "8px" }}>
          <Tooltip
            arrow={true}
            title={t("general.SHOW_ON_MAP_TOOLTIP")}
            placement={"top"}
            slotProps={{
              tooltip: {
                sx: tooltipStyles,
              },
            }}
          >
            <IconButton onClick={onMapOpen} disabled={disabled}>
              <AddLocationAlt />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Box sx={{ width: "100%", height: "400px" }}>
        <AddressBlockMap coordinate={currentCoordinate} readonly />
      </Box>
      {isMapOpen && (
        <AddressBlockRight
          label={label}
          value={currentValue}
          options={currentOptions}
          coordinate={currentCoordinate}
          onClose={handleClose}
          onSave={handleSave}
        />
      )}
    </Box>
  );
};
